import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
  CartesianGrid,
} from "recharts";
import { Activity, ShieldOff, Lock, Globe, Timer, Loader2, ShieldAlert, ArrowRight, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../App";
import { Card, Section } from "./ui";
import { ACTION_META, StatCard } from "./ui";

function Stat(props) {
  return <StatCard {...props} />;
}

export default function Dashboard() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);

  useEffect(() => { api.dashboard(session.tenant.id).then(setD); }, [session.tenant.id]);

  if (!d) return <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>;

  const decisionData = Object.entries(d.by_decision).map(([k, v]) => ({ name: k, value: v, color: ACTION_META[k].color }));
  // pad to a continuous last-14-days window so a single day doesn't render as one full-width bar
  const byDate = Object.fromEntries(d.timeline.map((r) => [r.date, r]));
  const timeline = Array.from({ length: 14 }).map((_, i) => {
    const dt = new Date();
    dt.setDate(dt.getDate() - (13 - i));
    const key = dt.toISOString().slice(0, 10);
    return byDate[key] || { date: key, ALLOW: 0, WARN: 0, MASK: 0, BLOCK: 0 };
  });

  return (
    <Section title="Admin Dashboard" subtitle="Live oversight metrics for this tenant.">
      {d.verification && d.verification.unverified >= 3 && (
        <button
          data-testid="verification-reminder"
          onClick={() => navigate("/audit?verification=unverified")}
          className="mb-5 flex w-full items-center gap-3 rounded-lg border border-warn/40 bg-warn/10 px-4 py-3 text-left transition-colors hover:bg-warn/15"
        >
          <ShieldAlert className="h-5 w-5 shrink-0 text-warn" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-warn">
              {d.verification.unverified} AI response{d.verification.unverified === 1 ? "" : "s"} still awaiting human review
            </div>
            <div className="text-[11px] text-slate-400">
              Only {d.verification.verified_pct}% of AI responses have been confirmed reviewed. Click to see the unverified records.
            </div>
          </div>
          <ArrowRight className="ml-auto h-4 w-4 shrink-0 text-warn" />
        </button>
      )}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-6">
        <Stat icon={Activity} label="Total interceptions" value={d.total} color="#0EA5E9" />
        <Stat icon={ShieldOff} label="Blocked" value={d.blocked_requests} color="#F43F5E" />
        <Stat icon={Lock} label="Masked requests" value={d.masked_requests} color="#06B6D4" />
        <Stat icon={Globe} label="EU sovereign route" value={`${d.eu_route_pct}%`} color="#3B82F6" />
        <Stat icon={ShieldCheck} label="Human-verified" value={`${d.verification?.verified_pct ?? 0}%`} sub={`${d.verification?.verified ?? 0}/${d.verification?.verifiable ?? 0} responses`} color="#10B981" testid="stat-human-verified" />
        <Stat icon={Timer} label="Avg latency" value={`${d.avg_latency_ms}`} sub="milliseconds" color="#EAB308" />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="p-5 lg:col-span-2">
          <h3 className="mb-4 font-display text-sm font-bold">Requests by decision · last 14 days</h3>
          {d.total === 0 ? (
            <Empty />
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={timeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E2A3C" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: "#64748B", fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={{ fill: "#64748B", fontSize: 10 }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#0D131D", border: "1px solid #1E2A3C", borderRadius: 8, fontSize: 12 }} />
                {["ALLOW", "WARN", "MASK", "BLOCK"].map((a) => (
                  <Bar key={a} dataKey={a} stackId="s" fill={ACTION_META[a].color} radius={a === "BLOCK" ? [3, 3, 0, 0] : 0} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="p-5">
          <h3 className="mb-4 font-display text-sm font-bold">Decision breakdown</h3>
          {d.total === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={230}>
              <PieChart>
                <Pie data={decisionData.filter((x) => x.value)} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3}>
                  {decisionData.map((e) => <Cell key={e.name} fill={e.color} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#0D131D", border: "1px solid #1E2A3C", borderRadius: 8, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
          <div className="mt-3 flex flex-wrap gap-3">
            {decisionData.map((e) => (
              <div key={e.name} className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-slate-400">
                <span className="h-2 w-2 rounded-full" style={{ background: e.color }} />{e.name} {e.value}
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="p-5">
          <h3 className="mb-4 font-display text-sm font-bold">Top detected categories</h3>
          {d.top_categories.length === 0 ? <Empty /> : (
            <div className="space-y-3">
              {d.top_categories.map((c) => {
                const max = d.top_categories[0].count || 1;
                return (
                  <div key={c.label}>
                    <div className="mb-1 flex justify-between text-xs"><span className="text-slate-300">{c.label}</span><span className="font-mono text-slate-500">{c.count}</span></div>
                    <div className="h-2 rounded-full bg-elevated"><div className="h-2 rounded-full bg-warn" style={{ width: `${(c.count / max) * 100}%` }} /></div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
        <Card className="p-5">
          <h3 className="mb-4 font-display text-sm font-bold">Provider routing split</h3>
          {d.providers.length === 0 ? <Empty /> : (
            <div className="space-y-3">
              {d.providers.map((p) => {
                const max = d.providers[0].count || 1;
                const isEu = p.name.includes("EU");
                return (
                  <div key={p.name}>
                    <div className="mb-1 flex justify-between text-xs"><span className="text-slate-300">{p.name}</span><span className="font-mono text-slate-500">{p.count}</span></div>
                    <div className="h-2 rounded-full bg-elevated"><div className="h-2 rounded-full" style={{ width: `${(p.count / max) * 100}%`, background: isEu ? "#3B82F6" : "#0EA5E9" }} /></div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {d.sector === "hr" && <BiasAuditView bias={d.bias} />}
    </Section>
  );
}

function BiasAuditView({ bias }) {
  const navigate = useNavigate();
  const b = bias || { flagged: 0, blocked: 0, monitored: 0, block_rate: 0, by_attribute: [] };
  const max = b.by_attribute.length ? b.by_attribute[0].count : 1;
  const drill = () => navigate("/audit?category=bias_sensitive&decision=BLOCK");
  return (
    <div className="mt-6" data-testid="bias-audit-view">
      <Card className="p-5 border-block/30">
        <div className="mb-4 flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-block" />
          <h3 className="font-display text-sm font-bold">Bias Audit View</h3>
          <span className="rounded-full bg-block/10 px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-block">HR</span>
          <button
            data-testid="bias-drilldown-btn"
            onClick={drill}
            className="ml-auto flex items-center gap-1.5 rounded-lg border border-subtle px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-300 hover:border-block/50 hover:text-block"
          >
            View blocked records <ArrowRight className="h-3 w-3" />
          </button>
        </div>
        <p className="mb-4 text-[11px] text-slate-500">
          How often bias-sensitive attributes (age, protected characteristics, photos) were caught by
          LucidGate and prevented from reaching an AI provider. Supports fair-hiring oversight — not a certification.
        </p>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <div className="rounded-lg bg-elevated p-4" data-testid="bias-flagged">
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Requests flagged</div>
            <div className="mt-1 font-display text-2xl font-extrabold text-white">{b.flagged}</div>
          </div>
          <button onClick={drill} className="rounded-lg bg-elevated p-4 text-left transition-colors hover:bg-block/10" data-testid="bias-blocked">
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Blocked</div>
            <div className="mt-1 font-display text-2xl font-extrabold text-block">{b.blocked}</div>
          </button>
          <div className="rounded-lg bg-elevated p-4" data-testid="bias-block-rate">
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Block rate</div>
            <div className="mt-1 font-display text-2xl font-extrabold text-white">{b.block_rate}%</div>
          </div>
          <div className="rounded-lg bg-elevated p-4" data-testid="bias-monitored">
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Monitor-only</div>
            <div className="mt-1 font-display text-2xl font-extrabold text-warn">{b.monitored}</div>
          </div>
        </div>

        <div className="mt-5">
          <h4 className="mb-3 font-mono text-[10px] uppercase tracking-widest text-slate-500">Bias-sensitive attributes detected</h4>
          {b.by_attribute.length === 0 ? (
            <div className="flex h-24 items-center justify-center text-sm text-slate-600">
              No bias-sensitive attributes detected yet — run HR prompts through Gateway Chat.
            </div>
          ) : (
            <div className="space-y-3" data-testid="bias-attribute-breakdown">
              {b.by_attribute.map((a) => (
                <button key={a.label} onClick={drill} className="block w-full text-left" data-testid="bias-attribute-row">
                  <div className="mb-1 flex justify-between text-xs">
                    <span className="text-slate-300">{a.label}</span>
                    <span className="font-mono text-slate-500">{a.count}</span>
                  </div>
                  <div className="h-2 rounded-full bg-base">
                    <div className="h-2 rounded-full bg-block" style={{ width: `${(a.count / max) * 100}%` }} />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

const Empty = () => (
  <div className="flex h-40 items-center justify-center text-sm text-slate-600">No data yet — run prompts through Gateway Chat.</div>
);
